import errorMessages from '@common/constants/errorMessages';
import {
  MOVE_IN_COSTS_NOTE_MAX_LENGTH,
  MOVE_IN_COSTS_PAYMENT_MIN_AMOUNT,
} from '@rental-manager/constants/PaymentConstants';
import { centsToDollars, dollarsToCents } from '@common/paymentsUtils/currencyUtils';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import { isEmpty, isObjectEmpty } from '@common/paymentsUtils/inputValidator';
import { addMonths } from 'date-fns';

export const isMoveInCostsNoteLengthValid = (value) => value.length <= MOVE_IN_COSTS_NOTE_MAX_LENGTH;

export const validateAmount = (key, amount, maxAmountCents, minAmountCents): Record<string, any> => {
  if (isEmpty(amount)) {
    return { [key]: errorMessages.MOVE_IN_COSTS_PAYMENT_AMOUNT_EMPTY };
  } else if (dollarsToCents(amount) > maxAmountCents) {
    const maxAmountInDollars = centsToDollars(maxAmountCents, { style: 'effectiveShort' });
    return { [key]: errorMessages.MOVE_IN_COSTS_PAYMENT_MAX_AMOUNT(maxAmountInDollars) };
  } else if (dollarsToCents(amount) < minAmountCents) {
    return { [key]: errorMessages.MOVE_IN_COSTS_PAYMENT_MIN_AMOUNT };
  }
  return {};
};

export const getMoveInCostsTotalLimitError = (limitCents: number): string =>
  errorMessages.MOVE_IN_COSTS_TOTAL_LIMIT(centsToDollars(limitCents, { style: 'effectiveShort' }));

/* Security Deposit Page */
export const validateSecurityDepositInputs = ({ moveInCosts, individualLimit, handleErrors }) => {
  const errors = validateAmount(
    'moveInCostsPaymentAmount',
    moveInCosts.moveInCostsPaymentAmount,
    individualLimit,
    MOVE_IN_COSTS_PAYMENT_MIN_AMOUNT,
  );
  if (!isMoveInCostsNoteLengthValid(moveInCosts.moveInCostsNotes)) {
    errors.moveInCostsNotes = errorMessages.MOVE_IN_COSTS_NOTE_MAX_LENGTH;
  }

  handleErrors(errors);
  return isObjectEmpty(errors);
};

/* Prorated Rent Page */
export const validateProratedRentInputs = ({ moveInCosts, individualLimit, handleErrors }) => {
  const errors = validateAmount(
    'moveInCostsPaymentAmount',
    moveInCosts.moveInCostsPaymentAmount,
    individualLimit,
    MOVE_IN_COSTS_PAYMENT_MIN_AMOUNT,
  );
  if (!isMoveInCostsNoteLengthValid(moveInCosts.moveInCostsNotes)) {
    errors.moveInCostsNotes = errorMessages.MOVE_IN_COSTS_NOTE_MAX_LENGTH;
  }

  handleErrors(errors);
  return isObjectEmpty(errors);
};

/* Other Page */
const validateMoveInCostsFeeTypeLengthValid = (value) => {
  if (isEmpty(value)) {
    return errorMessages.MOVE_IN_COSTS_FOR_EMPTY;
  }
  if (!isMoveInCostsNoteLengthValid(value)) {
    return errorMessages.MOVE_IN_COSTS_NOTE_MAX_LENGTH;
  }
  return '';
};

export const validateOtherPageInputs = ({ otherSettings, errorsList, individualLimit, handleErrors }) => {
  let errorExists = false;
  for (let idx = 0; idx < errorsList.length; idx++) {
    errorsList[idx].otherFeeType = validateMoveInCostsFeeTypeLengthValid(otherSettings[idx].otherFeeType);
    errorsList[idx].otherPaymentAmount = validateAmount(
      'otherPaymentAmount',
      otherSettings[idx].otherPaymentAmount,
      individualLimit,
      MOVE_IN_COSTS_PAYMENT_MIN_AMOUNT,
    ).otherPaymentAmount;
    errorsList[idx].otherNotes = isMoveInCostsNoteLengthValid(otherSettings[idx].otherNotes)
      ? ''
      : errorMessages.MOVE_IN_COSTS_NOTE_MAX_LENGTH;

    if (errorsList[idx].otherFeeType || errorsList[idx].otherPaymentAmount || errorsList[idx].otherNotes) {
      errorExists = true;
    }
  }

  handleErrors({ errorsList });
  return !errorExists;
};

/* Due Date Page */
const isDueDateEmpty = (value) => {
  const errors: Record<string, any> = {};
  if (isEmpty(value)) {
    errors.emptyError = errorMessages.MOVE_IN_COSTS_DUE_DATE_EMPTY;
  }
  return errors;
};

/**
 * @param {Date} value
 * @param {String} cutoffDate
 */
const isDueDateIncorrect = (value, cutoffDate) => {
  const errors: Record<string, any> = {};
  const dueDate = formatDate(value, 'yyyy-MM-dd');
  const today = formatDate(new Date(), 'yyyy-MM-dd');

  if (Date.parse(dueDate) < Date.parse(today)) {
    errors.pastDueDateError = errorMessages.MOVE_IN_COSTS_BEFORE_START_DATE;
  }

  if (Date.parse(dueDate) > Date.parse(cutoffDate)) {
    const formattedCutoffDate = formatDate(cutoffDate, 'MM/dd/yyyy');
    errors.pastCutoffDateError = errorMessages.MOVE_IN_COSTS_AFTER_CUTOFF_DATE(formattedCutoffDate);
  }

  return errors;
};

export const validateDueDateInputs = ({ dueDate, handleErrors }) => {
  const currentDate = new Date();
  const cutoffDate = addMonths(currentDate, 2);
  const errors = {
    ...isDueDateEmpty(dueDate),
    ...isDueDateIncorrect(dueDate, cutoffDate),
  };
  handleErrors(errors);
  return isObjectEmpty(errors);
};
